<template>
  <div>
    <div
      @click="hasChildren ? $emit('toggle', node.id) : $emit('select', node.id)"
      :class="[
        'group flex cursor-pointer items-center justify-between rounded-lg px-3 py-2 transition',
        isSelected
          ? 'bg-primary-50 font-medium text-primary-700'
          : 'text-slate-600 hover:bg-slate-50'
      ]"
      :style="{ paddingLeft: `${depth * 16 + 12}px` }"
    >
      <div class="flex min-w-0 items-center gap-2">
        <!-- 展开/折叠箭头 -->
        <button
          v-if="hasChildren"
          @click.stop="$emit('toggle', node.id)"
          class="flex h-4 w-4 shrink-0 items-center justify-center text-slate-400"
        >
          <svg
            :class="['h-3 w-3 transition-transform', isExpanded ? 'rotate-90' : '']"
            fill="none" stroke="currentColor" viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
          </svg>
        </button>
        <div v-else class="w-4 shrink-0"></div>

        <!-- 图标 -->
        <svg class="h-4 w-4 shrink-0 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
        </svg>

        <!-- 名称 -->
        <span
          class="truncate text-sm"
          @click.stop="$emit('select', node.id)"
        >{{ node.name }}</span>

        <!-- 状态标记 -->
        <span
          v-if="node.status === 1"
          class="shrink-0 rounded bg-red-100 px-1.5 py-0.5 text-[10px] text-red-600"
        >停用</span>
      </div>

      <!-- 操作按钮 -->
      <div v-if="showActions" class="flex shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
        <button
          @click.stop="$emit('add-child', node)"
          class="rounded p-1 text-slate-400 hover:text-primary-600 transition-colors"
          title="新增子分类"
        >
          <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
          </svg>
        </button>
        <button
          @click.stop="$emit('edit', node)"
          class="rounded p-1 text-slate-400 hover:text-primary-600 transition-colors"
          title="编辑"
        >
          <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        </button>
        <button
          @click.stop="$emit('delete', node)"
          class="rounded p-1 text-slate-400 hover:text-red-600 transition-colors"
          title="删除"
        >
          <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      </div>
    </div>

    <!-- 子节点 -->
    <template v-if="hasChildren && isExpanded">
      <KbCategoryTreeNode
        v-for="child in node.children"
        :key="child.id"
        :node="child"
        :depth="depth + 1"
        :selected-id="selectedId"
        :expanded-ids="expandedIds"
        :show-actions="showActions"
        @select="(id) => $emit('select', id)"
        @toggle="(id) => $emit('toggle', id)"
        @edit="(n) => $emit('edit', n)"
        @delete="(n) => $emit('delete', n)"
        @add-child="(n) => $emit('add-child', n)"
      />
    </template>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  node: { type: Object, required: true },
  depth: { type: Number, default: 0 },
  selectedId: { type: [Number, String], default: null },
  expandedIds: { type: Set, default: () => new Set() },
  showActions: { type: Boolean, default: true }
})

defineEmits(['select', 'toggle', 'edit', 'delete', 'add-child'])

const hasChildren = computed(() => props.node.children?.length > 0)
const isExpanded = computed(() => props.expandedIds.has(props.node.id))
const isSelected = computed(() => props.selectedId !== null && Number(props.selectedId) === props.node.id)
</script>
